import React from 'react';
import { Icon, type IconName } from '../primitives/Icon';
import { Text, XStack, YStack } from 'tamagui';
import { CachedImage } from './CachedImage';
import { colors } from '../theme/colors';

export type EntityPreviewVariant = 'trip' | 'day' | 'place' | 'transport' | 'custom';

export interface EntityPreviewProps {
  variant: EntityPreviewVariant;
  title: string;
  subtitle?: string | undefined;
  /** Tertiary line, e.g. "09:00 – 10:30" or a date range. */
  meta?: string | undefined;
  /** MaterialIcons name. Falls back to a per-variant default. */
  iconName?: IconName | undefined;
  /** Color for the icon / accent bar. Falls back to a per-variant default. */
  accentColor?: string | undefined;
  /** Photo URI (used by `place` variant). */
  imageUri?: string | undefined;
  /** Visual size — controls media size and typography. Default 'md'. */
  size?: 'sm' | 'md' | undefined;
}

const VARIANT_DEFAULTS: Record<
  EntityPreviewVariant,
  { iconName: IconName; accentColor: string }
> = {
  trip: { iconName: 'flight', accentColor: colors.accentCerulean },
  day: { iconName: 'calendar-today', accentColor: colors.accentCerulean },
  place: { iconName: 'place', accentColor: colors.accentCerulean },
  transport: { iconName: 'directions', accentColor: colors.accentOrange },
  custom: { iconName: 'event-note', accentColor: colors.accentGreen },
};

/**
 * Canonical entity preview header for any drawer/panel acting on a domain entity.
 *
 * Use as the `header` slot of `AdaptivePanel`, or anywhere you need to show
 * "what is being acted on" with consistent styling.
 *
 * `libs/ui` cannot import domain types, so consumers translate their entity
 * (Trip / Day / Place / Transport / CustomEntry) into `EntityPreviewProps` via
 * a small adapter. See `apps/mobile/src/lib/entity-preview.ts`.
 */
export function EntityPreview({
  variant,
  title,
  subtitle,
  meta,
  iconName,
  accentColor,
  imageUri,
  size = 'md',
}: EntityPreviewProps) {
  const defaults = VARIANT_DEFAULTS[variant];
  const icon = iconName ?? defaults.iconName;
  const accent = accentColor ?? defaults.accentColor;
  const mediaSize = size === 'sm' ? 36 : 48;
  const titleFontSize = size === 'sm' ? '$3' : '$4';

  return (
    <XStack
      alignItems="center"
      gap="$3"
      padding="$3"
      backgroundColor="$surface"
      borderRadius="$3"
    >
      <Media variant={variant} icon={icon} accent={accent} imageUri={imageUri} size={mediaSize} />
      <YStack flex={1}>
        <Text fontSize={titleFontSize} fontWeight="700" color="$textOnSurface" numberOfLines={1}>
          {title}
        </Text>
        {subtitle ? (
          <Text fontSize="$2" color="$textSubdued" numberOfLines={1}>
            {subtitle}
          </Text>
        ) : null}
        {meta ? (
          <Text fontSize="$1" color="$textSubdued">
            {meta}
          </Text>
        ) : null}
      </YStack>
    </XStack>
  );
}

function Media({
  variant,
  icon,
  accent,
  imageUri,
  size,
}: {
  variant: EntityPreviewVariant;
  icon: IconName;
  accent: string;
  imageUri?: string | undefined;
  size: number;
}) {
  if (imageUri) {
    return <CachedImage uri={imageUri} style={{ width: size, height: size, borderRadius: 6 }} />;
  }

  if (variant === 'day') {
    return <YStack width={4} height={size * 0.66} backgroundColor={accent} borderRadius={2} />;
  }

  return (
    <YStack
      width={size}
      height={size}
      borderRadius={6}
      backgroundColor="$sectionBgAlt"
      alignItems="center"
      justifyContent="center"
    >
      <Icon name={icon} size={Math.round(size * 0.5)} color={accent} />
    </YStack>
  );
}
